import { useEffect, useRef, useState } from "react";
import type { MatchClient } from "./match-client";
import type { Dict } from "./i18n";
import type { VisionView } from "./vision";
import { renderArena } from "./renderer";

/**
 * Canvas host.
 *
 * This component owns the `.arena-wrap` element, including for layout purposes.
 * It must be the only owner: an earlier version wrapped it in a second
 * `.arena-wrap` in the parent, and because the canvas is absolutely positioned,
 * the inner wrapper had no in-flow content and collapsed to zero height on
 * desktop. Overlays are passed as children so they position against the same
 * box the canvas measures.
 *
 * The render loop is decoupled from React on purpose: React owns the panels and
 * re-renders a few times per second, while the canvas paints every frame from a
 * mutable client object. Running the whole match through React state would mean
 * re-rendering the tree 60 times a second for no visual benefit.
 */
export function ArenaCanvas({
  client,
  speed,
  paused,
  d,
  visionSlot,
  readOnly = false,
  children,
}: {
  client: MatchClient;
  speed: number;
  paused: boolean;
  d: Dict;
  /** Which cricket's eyes to see through. Undefined is the spectator view. */
  visionSlot?: 0 | 1;
  /**
   * Paint the world without advancing it.
   *
   * Required when two canvases share one client - the broadcast and the vision
   * inset. Both would otherwise call `advance` on every frame, stepping the
   * simulation twice per tick and running the match at double speed while the
   * two views drifted apart.
   */
  readOnly?: boolean;
  children?: React.ReactNode;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const clientRef = useRef(client);
  const speedRef = useRef(speed);
  const pausedRef = useRef(paused);
  const dictRef = useRef(d);
  const visionSlotRef = useRef(visionSlot);
  const readOnlyRef = useRef(readOnly);
  const [, force] = useState(0);

  clientRef.current = client;
  speedRef.current = speed;
  pausedRef.current = paused;
  dictRef.current = d;
  visionSlotRef.current = visionSlot;
  readOnlyRef.current = readOnly;

  // Resume the render loop when the client instance changes (new match).
  useEffect(() => {
    force((n) => n + 1);
  }, [client]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;
    let last = performance.now();
    let lastUiSync = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      // Guard against a zero or unbounded box. The canvas is absolutely
      // positioned so it should never influence this measurement, but a
      // defensive clamp keeps a layout regression from becoming a runaway
      // allocation instead of just an ugly frame.
      const w = Math.max(1, Math.min(4000, Math.floor(rect.width)));
      const h = Math.max(1, Math.min(4000, Math.floor(rect.height)));
      const nextW = Math.floor(w * dpr);
      const nextH = Math.floor(h * dpr);
      if (canvas.width === nextW && canvas.height === nextH) return;
      canvas.width = nextW;
      canvas.height = nextH;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(wrap);

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const delta = Math.min(120, now - last);
      last = now;

      const active = clientRef.current;
      // A read-only canvas renders the world it is given. Advancing here as well
      // as in the broadcast would step the shared simulation twice per frame.
      if (!readOnlyRef.current && !pausedRef.current) {
        active.advance(delta, speedRef.current);
      } else {
        active.syncFloats();
      }

      // Recompute the vision view each frame from the snapshot. It is a pure
      // function of the current state, so this is cheap and cannot alter the
      // match. Computing it here rather than passing it as a prop means the mask
      // tracks the cricket at 60 Hz instead of at React's panel refresh rate.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const mode = visionSlotRef.current;
      renderArena(ctx, active, {
        width: canvas.width / dpr,
        height: canvas.height / dpr,
        alpha: alphaFor(active, speedRef.current, pausedRef.current),
        dict: dictRef.current,
        vision: mode === undefined ? null : active.visionFor(mode),
        ...(mode === undefined ? {} : { visionSlot: mode }),
      });

      // Drive React panels at a calm 6 Hz. Panels are for reading, not for per
      // frame updates, and throttling here keeps typing and scrolling smooth.
      if (now - lastUiSync > 160) {
        lastUiSync = now;
        force((n) => n + 1);
      }
    };
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [client]);

  return (
    <div className="arena-wrap" ref={wrapRef}>
      <canvas ref={canvasRef} />
      {children}
    </div>
  );
}

/**
 * Sub-tick interpolation factor.
 *
 * The world only advances on a 10 Hz grid, so without this the crickets would
 * visibly teleport ~30 units at a time. `alpha` lets the renderer draw them
 * partway between their last two positions.
 */
function alphaFor(client: MatchClient, speed: number, paused: boolean): number {
  if (paused) return 1;
  // At higher speeds each tick covers more wall-clock time, so a full second of
  // real-time smoothing would lag badly. Shortening the window keeps fast
  // playback crisp while normal playback stays smooth.
  const window = Math.max(0.05, 1 - Math.min(0.85, (speed - 1) * 0.2));
  return window;
}
